import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import { alpha, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  ApplicationSectionCard,
} from './applicationSectionChrome';
import type { IdentityNeighborhoodImageRef } from '../../api/identityVerificationDashboardApi';
import { identityVerificationDashboardApi } from '../../api/identityVerificationDashboardApi';
import {
  IDENTITY_MATCH_NEIGHBORHOOD_EMPTY,
  type IdentityMatchInsightRow,
  type IdentityMatchInsights,
} from '../../lib/identityMatchInsights';
import { messageFromIdentityVerificationHttpError } from '../../lib/identityVerificationHttpErrorMessage';
import { isObjectStorageKind } from '../../lib/storageKind';

function RoleChip({ row }: { row: IdentityMatchInsightRow }) {
  if (row.role === 'primary_hit') {
    return (
      <Chip
        size="small"
        color="error"
        label="Primary match"
        sx={{ height: 22, fontWeight: 800, letterSpacing: '0.01em' }}
      />
    );
  }
  return (
    <Chip
      size="small"
      color="warning"
      variant="outlined"
      label={`Near #${row.rank - 1}`}
      sx={{
        height: 22,
        fontWeight: 700,
        bgcolor: (t) => alpha(t.palette.warning.main, 0.1),
      }}
    />
  );
}

function CellLabel({ children }: { children: string }) {
  return (
    <Typography
      component="span"
      sx={{
        display: { xs: 'inline', md: 'none' },
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        mr: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

async function resolveNeighborhoodImageUrl(ref: IdentityNeighborhoodImageRef): Promise<{
  url: string;
  contentType: string;
  revokeOnCleanup: boolean;
}> {
  if (isObjectStorageKind(ref.storage_kind)) {
    try {
      const access = ref.uploaded_image_id
        ? await identityVerificationDashboardApi.uploadedImageAccessUrl(ref.uploaded_image_id)
        : await identityVerificationDashboardApi.storageAccessUrl(ref.file_path);
      return { url: access.url, contentType: access.contentType || 'image/jpeg', revokeOnCleanup: false };
    } catch {
      // fall through
    }
  }

  const blob = ref.uploaded_image_id
    ? await identityVerificationDashboardApi.uploadedImageContent(ref.uploaded_image_id)
    : await identityVerificationDashboardApi.storageContentByPath(ref.file_path);
  return {
    url: URL.createObjectURL(blob),
    contentType: (blob.type || 'image/jpeg').toLowerCase(),
    revokeOnCleanup: true,
  };
}

function ShowPhotoButton({
  label,
  image,
  onShown,
}: {
  label: string;
  image: IdentityNeighborhoodImageRef | null;
  onShown: (payload: { title: string; url: string; contentType: string; revoke: boolean }) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!image) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Stack spacing={0.35} alignItems={{ xs: 'flex-start', md: 'stretch' }}>
      <Button
        size="small"
        variant="outlined"
        disabled={loading}
        onClick={async () => {
          setError(null);
          setLoading(true);
          try {
            const resolved = await resolveNeighborhoodImageUrl(image);
            onShown({
              title: label,
              url: resolved.url,
              contentType: resolved.contentType,
              revoke: resolved.revokeOnCleanup,
            });
          } catch (err: unknown) {
            setError(messageFromIdentityVerificationHttpError(err) || 'Could not load photo.');
          } finally {
            setLoading(false);
          }
        }}
        sx={{
          minWidth: 64,
          height: 28,
          px: 1.25,
          fontWeight: 700,
          fontSize: '0.72rem',
          textTransform: 'none',
        }}
        startIcon={loading ? <CircularProgress size={12} color="inherit" /> : undefined}
      >
        {loading ? 'Loading' : 'Show'}
      </Button>
      {error ? (
        <Typography variant="caption" color="error" sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}>
          {error}
        </Typography>
      ) : null}
    </Stack>
  );
}

export function IdentityMatchInsightsGrid({
  insights,
}: {
  insights: IdentityMatchInsights;
}) {
  const theme = useTheme();
  const [preview, setPreview] = useState<{
    title: string;
    url: string;
    contentType: string;
    revoke: boolean;
  } | null>(null);

  const isEmpty = insights.rows.length === 0;

  const cols = {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      md: 'minmax(118px, 130px) minmax(0, 1.35fr) minmax(0, 1.1fr) minmax(0, 0.95fr) minmax(0, 0.85fr) minmax(72px, 88px) minmax(72px, 88px)',
    },
    columnGap: 1.25,
    rowGap: 0.35,
    alignItems: 'center',
  } as const;

  const closePreview = () => {
    setPreview((prev) => {
      if (prev?.revoke) URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  return (
    <ApplicationSectionCard
      title="Match neighborhood"
      subtitle="Primary duplicate hit and closest customers from face, DOB, name, address, and document checks"
      icon={<HubRoundedIcon fontSize="small" />}
      endAdornment={
        <Chip
          size="small"
          variant="outlined"
          label={
            isEmpty
              ? 'No matches'
              : `${insights.rows.length} record${insights.rows.length === 1 ? '' : 's'}`
          }
          sx={{ height: 22, fontWeight: 700 }}
        />
      }
    >
      {isEmpty ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.5, maxWidth: 560 }}
        >
          {IDENTITY_MATCH_NEIGHBORHOOD_EMPTY}
        </Typography>
      ) : (
        <>
          {insights.matchSummary ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1.5, lineHeight: 1.45, maxWidth: 720 }}
            >
              {insights.matchSummary}
            </Typography>
          ) : null}

          <Box
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              bgcolor:
                theme.palette.mode === 'light'
                  ? alpha(theme.palette.common.black, 0.015)
                  : alpha(theme.palette.common.white, 0.03),
            }}
          >
            <Box
              sx={{
                ...cols,
                px: 1.5,
                py: 1,
                display: { xs: 'none', md: 'grid' },
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor:
                  theme.palette.mode === 'light'
                    ? alpha(theme.palette.primary.main, 0.04)
                    : alpha(theme.palette.common.white, 0.04),
              }}
            >
              {['Role', 'Customer', 'Customer id', 'Email', 'Phone', 'Selfie', 'Document'].map(
                (h) => (
                  <Typography
                    key={h}
                    variant="caption"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: '0.07em',
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      fontSize: '0.65rem',
                    }}
                  >
                    {h}
                  </Typography>
                ),
              )}
            </Box>

            <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
              {insights.rows.map((row, i) => {
                const isHit = row.role === 'primary_hit';
                const accent = isHit ? theme.palette.error.main : theme.palette.warning.main;
                const washA = isHit
                  ? alpha('#B42318', theme.palette.mode === 'light' ? 0.07 : 0.14)
                  : alpha('#B45309', theme.palette.mode === 'light' ? 0.06 : 0.12);
                const washB = isHit
                  ? alpha('#C2410C', theme.palette.mode === 'light' ? 0.05 : 0.1)
                  : alpha('#CA8A04', theme.palette.mode === 'light' ? 0.05 : 0.1);
                return (
                  <Box
                    key={row.id}
                    component={motion.div}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.28, delay: i * 0.05, ease: 'easeOut' }}
                    sx={{
                      ...cols,
                      px: 1.5,
                      py: 1.25,
                      position: 'relative',
                      backgroundImage: `linear-gradient(105deg, ${washA} 0%, ${washB} 42%, transparent 78%)`,
                      bgcolor:
                        theme.palette.mode === 'light'
                          ? alpha(theme.palette.common.black, 0.01)
                          : alpha(theme.palette.common.white, 0.02),
                      boxShadow: `inset 3px 0 0 ${alpha(accent, 0.95)}`,
                      '&:hover': {
                        backgroundImage: `linear-gradient(105deg, ${alpha(accent, theme.palette.mode === 'light' ? 0.12 : 0.2)} 0%, ${washB} 50%, transparent 85%)`,
                      },
                    }}
                  >
                    <Box>
                      <CellLabel>Role</CellLabel>
                      <RoleChip row={row} />
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <CellLabel>Customer</CellLabel>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 800,
                          lineHeight: 1.3,
                          letterSpacing: '0.01em',
                          fontSize: '0.875rem',
                          // Rose → amber for primary; amber → clay for near (not Follow-through teal).
                          backgroundImage: isHit
                            ? 'linear-gradient(105deg, #B42318 0%, #C2410C 48%, #B45309 100%)'
                            : 'linear-gradient(105deg, #B45309 0%, #CA8A04 55%, #A16207 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          color: 'transparent',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        {row.name ?? '—'}
                      </Typography>
                      {row.accountStatus ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15 }}>
                          {row.accountStatus.replace(/_/g, ' ')}
                        </Typography>
                      ) : null}
                      {row.matchSignals.length > 0 ? (
                        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                          {row.matchSignals.slice(0, 4).map((signal) => {
                            const chip = (
                              <Chip
                                size="small"
                                label={signal.label}
                                variant="outlined"
                                sx={{
                                  height: 18,
                                  fontSize: '0.62rem',
                                  fontWeight: 700,
                                  maxWidth: '100%',
                                  cursor: signal.tooltip ? 'help' : 'default',
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            );
                            if (!signal.tooltip) return <Box key={signal.id}>{chip}</Box>;
                            return (
                              <Tooltip
                                key={signal.id}
                                title={
                                  <Typography
                                    component="span"
                                    sx={{ whiteSpace: 'pre-line', fontSize: '0.75rem', lineHeight: 1.4 }}
                                  >
                                    {signal.tooltip}
                                  </Typography>
                                }
                                arrow
                                enterDelay={200}
                              >
                                <Box component="span" sx={{ display: 'inline-flex' }}>
                                  {chip}
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Stack>
                      ) : null}
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <CellLabel>Customer id</CellLabel>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          wordBreak: 'break-all',
                          lineHeight: 1.35,
                        }}
                      >
                        {row.ccid}
                      </Typography>
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <CellLabel>Email</CellLabel>
                      <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all', lineHeight: 1.35 }}>
                        {row.email ?? '—'}
                      </Typography>
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <CellLabel>Phone</CellLabel>
                      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.35 }}>
                        {row.phone ?? '—'}
                      </Typography>
                    </Box>

                    <Box>
                      <CellLabel>Selfie</CellLabel>
                      <ShowPhotoButton
                        label={`Selfie · ${row.name ?? row.ccid}`}
                        image={row.selfie}
                        onShown={setPreview}
                      />
                    </Box>

                    <Box>
                      <CellLabel>Document</CellLabel>
                      <ShowPhotoButton
                        label={`Document · ${row.name ?? row.ccid}`}
                        image={row.document}
                        onShown={setPreview}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>

          <Dialog open={!!preview} onClose={closePreview} maxWidth="md" fullWidth>
            <DialogTitle sx={{ pr: 6, position: 'relative' }}>
              {preview?.title ?? 'Photo'}
              <IconButton
                aria-label="Close"
                onClick={closePreview}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseRoundedIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {preview ? (
                <Box
                  component="img"
                  src={preview.url}
                  alt={preview.title}
                  sx={{
                    display: 'block',
                    width: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    bgcolor: alpha(theme.palette.common.black, 0.04),
                    borderRadius: 1,
                  }}
                />
              ) : null}
            </DialogContent>
          </Dialog>
        </>
      )}
    </ApplicationSectionCard>
  );
}
